<script lang="ts">
    import '$src/app.postcss';
    import { onMount, tick } from 'svelte';
    import { Loader } from '@allibee/components/common';
    import { Modal, popNonPersistentToasts, Toast } from '@allibee/components/basic';
    import { browser, dev } from '$app/environment';
    import { afterNavigate, beforeNavigate, goto, replaceState } from '$app/navigation';
    import { page } from '$app/stores';
    import { blur } from 'svelte/transition';
    import { cubicIn } from 'svelte/easing';
    import { modal } from '$stores/modalStore';
    import ResizeObserver from 'resize-observer-polyfill';
    import { isEqual, isEmpty, debounce } from 'lodash-es';
    import { isLoadingOverlay } from '$stores/loadingStore';
    import { clearAbortedOverlayCount, hideLoadingOverlay, incrementAbortedOverlayCount, showLoadingOverlay } from '$lib/utils';
    import { handleRootModalBeforeNavigate } from '$lib/utils/modalNavigation';
    import DevHelpWidget from './DevHelpWidget.svelte';
    import 'core-js/proposals/change-array-by-copy';
    // polyfill for https://github.com/tc39/proposal-change-array-by-copy'
    import 'core-js/modules/web.structured-clone.js'; // polyfill for structuredClone
    import 'core-js/proposals/relative-indexing-method';
    import { _ } from 'svelte-i18n';
    import { HeadTitle, PwaLoader } from '$lib/service';
    import { base } from '$app/paths';
    import { initializePostHog, refreshToken, shouldRefreshToken } from '$stores/auth';
    import { initConversionTracking, trackEvent } from '@allibee/service';
    import { captureSignupSource } from '$lib/service/signUp/signupSource';
    import { consumeBusinessAgentMfeParentRevalidationTransition } from '$lib/mfe/businessAgentMfeRouteTransition';

    const isDevServerEnv = __IS_DEV_SERVER_ENV__;

    if (browser) window.ResizeObserver = ResizeObserver; // Polyfill

    export let data;

    // TODO: https://github.com/sveltejs/kit/issues/11956 이슈로 인한 임시 코드
    // shallow routing 으로 구현된 replaceState의 경우 history.state를 초기화 시키기는 하지만 동기화가 되지않는 문제가 있다.
    // 또한 페이지 로딩시 $page.state를 초기화해버리는 문제가 존재해서 onMount 단계에서는 history.state 의 값을 정확히 알아오지 못하는 문제가 발생한다.
    // afterNavigate 단계에서는 정상적인 $page.state 를 읽을 수 있다
    $: {
        const sveltekitStates = browser ? history.state?.['sveltekit:states'] : undefined;

        if (browser && isEmpty($page.state) && !isEmpty(sveltekitStates)) {
            goto('', { replaceState: true, state: sveltekitStates });
            setTimeout(() => {
                replaceState('', sveltekitStates);
            });
        }
    }
    $: ({ pathname, session } = data);

    // 페이지 전환시 beforeNavigate.to 가 afterNavigate.to 와 항상 같지는 않다. 예를들어 라우팅 경로 하위의 디폴트 경로로 이동하게 되는 경우가 그렇다.
    let lastBeforeNavigateFrom;
    let lastBeforeNavigateTo;
    let rootPageSlotKey = '';
    let renderedRootPageSlotPathname = '';
    let pendingBusinessAgentMfeParentRevalidation: { readonly targetKey: string } | null = null;

    $: if (pathname !== renderedRootPageSlotPathname) {
        renderedRootPageSlotPathname = pathname;
        if (pendingBusinessAgentMfeParentRevalidation === null) {
            rootPageSlotKey = pathname;
        }
    }

    function getRouteTargetKey(url: URL) {
        return `${url.pathname}${url.search}`;
    }

    beforeNavigate(async ({ from, to, willUnload, type, cancel, complete }) => {
        if (__DEBUG_LOADING_OVERLAY__) {
            console.groupCollapsed(`Navigate Root Layout ${from?.url.pathname} -> ${to?.url.pathname}`);
            console.log('beforeNavigate', { from, to, willUnload, type });
        }

        const skipsRootRouteTransition = Boolean(from && to && consumeBusinessAgentMfeParentRevalidationTransition(from.url, to.url));
        const targetKey = to ? getRouteTargetKey(to.url) : null;
        if (skipsRootRouteTransition && targetKey) {
            const pendingTransition = { targetKey };
            pendingBusinessAgentMfeParentRevalidation = pendingTransition;
            // SvelteKit observes the original `complete` rejection internally. This observer
            // only removes a cancelled transition marker; using `finally` here would create a
            // second rejected promise that no caller observes.
            void complete.then(undefined, () => {
                if (pendingBusinessAgentMfeParentRevalidation === pendingTransition) {
                    pendingBusinessAgentMfeParentRevalidation = null;
                }
            });
        } else {
            pendingBusinessAgentMfeParentRevalidation = null;
        }

        const routeChanged = !!to?.route.id && (to?.route.id !== from?.route.id || !isEqual(to?.params, from?.params));
        const suppressesRootRouteLoadingOverlay = pendingBusinessAgentMfeParentRevalidation !== null;
        const shouldShowRouteLoadingOverlay = routeChanged && !suppressesRootRouteLoadingOverlay;
        const isModalNavigationHandled = handleRootModalBeforeNavigate({
            modals: $modal,
            type,
            cancel,
            closeModal: (id, value) => modal.close(id, value),
            incrementAbortedOverlayCount,
            shouldIncrementAbortedOverlayCount: shouldShowRouteLoadingOverlay,
        });

        if (shouldShowRouteLoadingOverlay) {
            lastBeforeNavigateFrom = from;
            lastBeforeNavigateTo = to;
            showLoadingOverlay();

            await tick(); // beforeNavigate의 경우 상위페이지부터 호출되기 때문에 다음 프레임으로 넘기기 위해 tick()을 사용한다.
            popNonPersistentToasts();
            // 페이지 단위 beforeNavigate 이벤트에서 navigation을 취소하는 경우
            clearAbortedOverlayCount();
        }
        if (isModalNavigationHandled) return;
    });
    afterNavigate(({ from, to, willUnload, type }) => {
        // 어떤 경로로 진입하든 signupSource 쿼리 파라미터를 보관해 이후 회원가입 요청에 사용한다.
        if (to?.url) captureSignupSource(to.url);

        const routeChanged = !!to?.route.id && (to?.route.id !== from?.route.id || !isEqual(to?.params, from?.params));
        const targetKey = to ? getRouteTargetKey(to.url) : null;
        const preservesRootPageSlot = targetKey !== null && pendingBusinessAgentMfeParentRevalidation?.targetKey === targetKey;
        const suppressesRootRouteLoadingOverlay = pendingBusinessAgentMfeParentRevalidation !== null;

        if (!from?.url || type === 'enter') {
            // 서비스 첫 진입시
        } else if (routeChanged && !suppressesRootRouteLoadingOverlay) {
            hideLoadingOverlay();
        } else if (lastBeforeNavigateFrom && lastBeforeNavigateTo && from?.route.id === lastBeforeNavigateFrom.route.id && to?.route.id !== lastBeforeNavigateTo.route.id) {
            // 페이지 전환중에 페이지 전환을 취소하고 다른 페이지로 이동하는 경우
            lastBeforeNavigateFrom = undefined;
            lastBeforeNavigateTo = undefined;

            hideLoadingOverlay();
        }
        if (pendingBusinessAgentMfeParentRevalidation !== null && !preservesRootPageSlot) {
            rootPageSlotKey = pathname;
        }
        if (pendingBusinessAgentMfeParentRevalidation !== null) {
            pendingBusinessAgentMfeParentRevalidation = null;
        }
        if (__DEBUG_LOADING_OVERLAY__) {
            console.log('afterNavigate', { from, to, willUnload, type });
            console.groupEnd();
        }

        if ($page.data.themeConfig.sendAnalyticsEvent) {
            trackEvent('page_view', {
                properties: {
                    path: window.location.pathname,
                    from: from?.url?.pathname ?? null,
                    to: to?.url.pathname ?? null,
                },
            });
        }
    });

    let modalWrapperRefs: HTMLDivElement[] = [];

    $: (modalWrapperRefs.filter(Boolean).at(-1)?.querySelector('[tabindex="-1"][role="dialog"]') as HTMLElement | null)?.focus();

    const GTM_ID = 'GTM-KWSJ52TH';

    // GTM 스크립트 자체는 async지만, svelte:head에 넣으면 초기 HTML 파싱 시점에 곧바로
    // 요청이 발생해 LCP 이전 대역폭을 잠식한다. idle 시점으로 미뤄 주요 콘텐츠 로딩과
    // 경쟁하지 않게 한다 (dataLayer push는 GTM 스크립트가 이벤트를 놓치지 않도록 즉시 실행).
    const GTM_SCRIPT_ID = 'bhsn-gtm-script';

    function loadGtmDeferred() {
        const w = window as typeof window & { dataLayer?: unknown[] };
        w.dataLayer = w.dataLayer || [];
        w.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

        const inject = () => {
            if (document.getElementById(GTM_SCRIPT_ID)) return; // onMount 재실행 등으로 인한 중복 삽입 방지
            const script = document.createElement('script');
            script.id = GTM_SCRIPT_ID;
            script.async = true;
            script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
            document.head.appendChild(script);
        };

        if ('requestIdleCallback' in window) {
            window.requestIdleCallback(inject, { timeout: 4000 });
        } else {
            setTimeout(inject, 1);
        }
    }

    // onMount 1회 체크가 아니라 반응형으로 둔다 — 루트 레이아웃은 SPA 세션 동안 한 번만
    // 마운트되므로, 최초 마운트 시점엔 sendGMTEvent가 false였다가 이후 다른 workspace/theme로
    // 클라이언트 사이드 이동하며 true로 바뀌는 경우 onMount 1회 체크로는 영영 로드되지 않는다
    // (제거된 이전 svelte:head {#if} 방식은 반응형이라 이 경우를 커버했다 — 회귀 방지).
    //
    // gtmLoadTriggered 가드가 반드시 필요하다: $page는 네비게이션마다 갱신되는 스토어라
    // sendGMTEvent가 true로 유지되는 동안엔 이 반응형 statement 자체가 매 네비게이션마다
    // 재실행된다. inject() 내부의 DOM 중복 체크는 <script> 태그 중복 삽입만 막을 뿐,
    // loadGtmDeferred()가 매번 다시 호출되면 그 안의 dataLayer.push({event:'gtm.js'})가
    // 매 네비게이션마다 다시 실행되어 GTM 부트스트랩 이벤트가 중복 발생한다(gtm.js 트리거
    // 태그 이중 집계). 이 플래그로 loadGtmDeferred() 자체를 세션당 정확히 1회로 제한한다.
    let gtmLoadTriggered = false;
    $: if (browser && $page.data.themeConfig.sendGMTEvent && !gtmLoadTriggered) {
        gtmLoadTriggered = true;
        loadGtmDeferred();
    }

    // 마케팅 가입 전환 트래킹 — 구글 gtag·네이버 wcs 직삽입 (GTM 미사용). GTM 로더와 같은 이유로 반응형 1회 트리거.
    let conversionTrackingTriggered = false;
    $: if (browser && $page.data.themeConfig.sendGMTEvent && !conversionTrackingTriggered) {
        conversionTrackingTriggered = true;
        initConversionTracking(true);
    }

    onMount(() => {
        if (browser && $page.data.themeConfig.sendAnalyticsEvent && !dev) {
            initializePostHog();
        }

        const handleRefreshOnUserAction = debounce(async () => {
            if (!session || !shouldRefreshToken()) return;
            await refreshToken();
        }, 1000);

        window.addEventListener('mousemove', handleRefreshOnUserAction, true);
        window.addEventListener('keydown', handleRefreshOnUserAction, true);

        return () => {
            window.removeEventListener('mousemove', handleRefreshOnUserAction, true);
            window.removeEventListener('keydown', handleRefreshOnUserAction, true);
        };
    });
</script>

<svelte:head>
    {#each $page.data.themeConfig.fontLinks as fontLink}
        <link rel="stylesheet" href="{base}{fontLink}" as="style" crossorigin="anonymous" />
    {/each}
    <link rel="icon" href={$page.data.themeConfig.faviconHref} />
</svelte:head>

{#if $page.data.themeConfig.sendGMTEvent}
    <!-- Google Tag Manager (noscript) -->
    <noscript><iframe title="Google Tag Manager" src="https://www.googletagmanager.com/ns.html?id={GTM_ID}" height="0" width="0" style="display: none; visibility: hidden"></iframe></noscript>
    <!-- End Google Tag Manager (noscript) -->
{/if}
<HeadTitle title={$page.data.title} />
<PwaLoader />

<!-- 페이지 전환중 로딩바 -->
<div>
    <!--
        sveltekit2 버전의 문제인지 원인은 정확히 모르겠지만 최상위 layout.svelte의 최상위 dom이 #if로 제어가 될때
        하이드레이션 동작에 문제가 발생하는듯하다. 그래서 최상위 dom은 항상 렌더링되도록 하고, 내부의 dom을 제어하도록 한다.
    -->
    {#if $isLoadingOverlay}
        <div class="fixed z-[110] flex h-screen w-screen items-center justify-center">
            <div class="flex h-12 w-12 items-center justify-center">
                <Loader />
            </div>
        </div>
    {/if}
</div>

<!-- 본문 -->
<div class="width-constraint h-0 min-h-screen w-full" class:blur={$isLoadingOverlay}>
    {#key rootPageSlotKey}
        <div class="h-full" in:blur={{ easing: cubicIn, duration: 300 }}>
            <slot />
        </div>
    {/key}

    <!-- global functional modal -->
    {#each $modal as { Component, props, modalProps, id, type }, i (id)}
        <div bind:this={modalWrapperRefs[i]}>
            {#if type === 'fullcustom'}
                <svelte:component this={Component} on:close={e => modal.close(id, e.detail)} on:confirm={e => modal.close(id, e.detail)} on:cancel={() => modal.close(id, false)} {...props} />
            {:else}
                <Modal open {...modalProps} on:outroend={() => modal.close(id, ['confirm', 'prompt', 'custom'].includes(type) ? false : true)} zIndexBackdrop={40 + i * 10} zIndexModal={45 + i * 10}>
                    <svelte:component
                        this={Component}
                        on:confirm={e => modal.close(id, e.detail)}
                        on:cancel={() => modal.close(id, false)}
                        confirmText={$_('lib.utils.modal.confirm')}
                        cancelText={$_('lib.utils.modal.cancel')}
                        {...props}
                    />
                </Modal>
            {/if}
        </div>
    {/each}
</div>
<!-- global toast -->
<Toast />

<!-- 개발자 도움 위젯 -->
{#if isDevServerEnv}
    <DevHelpWidget />
{/if}
